import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { useRef, useState } from 'react';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import GroupsAutocomplete from '../../../shared/groups-autocomplete';
import z from 'zod';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Delete from '@mui/icons-material/Delete';

interface RemoveFromGroupActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
  trigger: UseFormTrigger<PolicyFormValues>;
}

export const RemoveFromGroupActionSchema = z
  .object({
    type: z.literal('Remove_From_Group'),
    groupEmail: z.array(z.string()).nullable(),
    removeAllGroups: z.string(),
  })
  .superRefine((data, ctx) => {
    if (
      data.removeAllGroups !== 'true' &&
      (!data.groupEmail || data.groupEmail.length === 0)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['groupEmail'],
        message: 'Please select a group or all groups to remove.',
      });
    }
  });

export type RemoveFromGroupActionType = z.infer<
  typeof RemoveFromGroupActionSchema
>;

// Type guard to check if an action is RemoveFromGroupActionType
function isRemoveFromGroupAction(
  action: PolicyAction
): action is RemoveFromGroupActionType {
  return action.type === 'Remove_From_Group';
}

export default function RemoveFromGroupAction({
  setValue,
  watch,
  control,
  errors,
  trigger,
}: RemoveFromGroupActionProps) {
  const [alreadySelectedError, setAlreadySelectedError] = useState(false);
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const autocompleteKey = useRef(new Date().getTime().toString());
  const actionData = watch('actionData');
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Remove_From_Group');
  const actionAtIndex = actionData[idx];
  if (!isRemoveFromGroupAction(actionAtIndex)) {
    return null;
  }

  const currentAction: RemoveFromGroupActionType = actionAtIndex;
  const error: FieldErrors<RemoveFromGroupActionType> | undefined =
    errors?.actionData?.[idx];

  function removeSelection(selectedGroup: string) {
    const newList =
      currentAction.groupEmail?.filter((group) => group !== selectedGroup) ??
      [];
    setValue(`actionData.${idx}.groupEmail`, [...newList]);
  }

  return (
    <Stack sx={{ p: 1 }}>
      <Stack>
        <Stack flexDirection={'row'} gap={1}>
          <Box sx={{ width: 400 }}>
            <Controller
              name={`actionData.${idx}.groupEmail`}
              control={control}
              render={() => (
                <GroupsAutocomplete
                  newKey={`${autocompleteKey.current}`}
                  autocompleteValue={autoCompleteResponse}
                  setAutoCompleteValue={(value) => {
                    if (!value) {
                      setAlreadySelectedError(false);
                      return;
                    }
                    if (currentAction.groupEmail?.includes(value)) {
                      setAlreadySelectedError(true);
                    } else {
                      setAlreadySelectedError(false);
                      setValue(`actionData.${idx}.groupEmail`, [
                        ...(currentAction.groupEmail ?? []),
                        value,
                      ]);
                      trigger(`actionData.${idx}.groupEmail`);
                      autocompleteKey.current = new Date().getTime().toString();
                      setAutoCompleteResponse('');
                    }
                  }}
                  errors={!!error?.groupEmail || alreadySelectedError}
                  helperText={
                    alreadySelectedError
                      ? 'Group is already selected.'
                      : error
                      ? error.groupEmail?.message
                      : ' '
                  }
                />
              )}
            />
          </Box>
        </Stack>
        <FormControlLabel
          control={
            <Checkbox
              checked={currentAction.removeAllGroups === 'true'}
              onChange={() => {
                setValue(
                  `actionData.${idx}.removeAllGroups`,
                  currentAction.removeAllGroups === 'true' ? 'false' : 'true'
                );
                setValue(`actionData.${idx}.groupEmail`, []);
                if (currentAction.removeAllGroups === 'true') {
                  trigger(`actionData.${idx}.groupEmail`);
                }
              }}
            />
          }
          sx={{ width: 'fit-content' }}
          label="Remove From All Groups"
        />
        <Box sx={{ maxHeight: 197 }}>
          <Paper sx={{ minWidth: 470, overflow: 'hidden', maxHeight: 197 }}>
            <TableContainer sx={{ minWidth: 470, maxHeight: 197 }}>
              <Table size={'small'} stickyHeader aria-label="selected-groups">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {currentAction.removeAllGroups === 'true'
                        ? 'Groups to Keep'
                        : 'Groups to Remove'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentAction.groupEmail
                    ? currentAction.groupEmail.map((group) => (
                        <TableRow key={group}>
                          <TableCell>
                            <Stack
                              flexDirection="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Typography variant="body2">{group}</Typography>
                              <Tooltip arrow title="Delete">
                                <span>
                                  <IconButton
                                    onClick={() => {
                                      removeSelection(group);
                                    }}
                                  >
                                    <Delete />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Stack>
    </Stack>
  );
}
